/**
 * 統一的影片統計數據計算工具
 * 確保同一支影片在所有分頁都顯示相同的統計數據
 */

export interface VideoStats {
  checkpointCount: number;
  attentionCount: number;
  favoriteCount: number;
}

/**
 * 統一計算影片統計數據
 * @param video 影片數據對象
 * @returns 統一的統計數據
 */
export function calculateVideoStats(video: any): VideoStats {
  // 🔧 統一計算重點數
  let checkpointCount = 0;
  
  // 優先使用實際的 vbids 長度（最準確）
  if (Array.isArray(video.vbids) && video.vbids.length > 0) {
    checkpointCount = video.vbids.length;
  } else if (Array.isArray(video.numberKeys) && video.numberKeys.length > 0) {
    // 回退到 numberKeys 長度
    checkpointCount = video.numberKeys.length;
  } else if (video.checkpointCount && typeof video.checkpointCount === 'number') {
    // 回退到 checkpointCount 字段
    checkpointCount = video.checkpointCount;
  } else if (video.globalCheckpointCount && typeof video.globalCheckpointCount === 'number') {
    // 回退到 globalCheckpointCount 字段
    checkpointCount = video.globalCheckpointCount;
  } else if ((video as any).userNotesCount && typeof (video as any).userNotesCount === 'number') {
    // 回退到 userNotesCount
    checkpointCount = (video as any).userNotesCount;
  } else if ((video as any).personalCheckpointCount && typeof (video as any).personalCheckpointCount === 'number') {
    // 最後回退到 personalCheckpointCount
    checkpointCount = (video as any).personalCheckpointCount;
  }

  // 🔧 統一計算關注度
  let attentionCount = 0;
  if (video.attentionCount && typeof video.attentionCount === 'number') {
    attentionCount = video.attentionCount;
  } else if (video.checkpointCount && typeof video.checkpointCount === 'number') {
    // 如果沒有 attentionCount，使用 checkpointCount 作為關注度指標
    attentionCount = video.checkpointCount;
  }

  // 🔧 統一計算收藏數
  let favoriteCount = 0;
  if (video.favoriteCount && typeof video.favoriteCount === 'number') {
    favoriteCount = video.favoriteCount;
  } else if (video.readUsers && Array.isArray(video.readUsers)) {
    // 使用 readUsers 數組長度
    favoriteCount = video.readUsers.length;
  } else if (video.readCount && typeof video.readCount === 'number') {
    // 使用 readCount 字段
    favoriteCount = video.readCount;
  } else if (video.totalReadCount && typeof video.totalReadCount === 'number') {
    // 使用 totalReadCount 字段
    favoriteCount = video.totalReadCount;
  } else {
    // 如果都沒有，使用 checkpointCount 作為收藏數的指標（最少為 1）
    favoriteCount = Math.max(1, Math.floor(checkpointCount / 10));
  }

  return {
    checkpointCount,
    attentionCount,
    favoriteCount
  };
}

/**
 * 為影片對象添加統一的統計數據（同步版本 - 不檢查字幕緩存）
 * @param video 影片數據對象
 * @returns 添加了統一統計數據的影片對象
 */
export function addUnifiedStats(video: any): any {
  const stats = calculateVideoStats(video);
  
  return {
    ...video,
    // 添加統一的統計數據字段
    unifiedCheckpointCount: stats.checkpointCount,
    unifiedAttentionCount: stats.attentionCount,
    unifiedFavoriteCount: stats.favoriteCount,
    // 覆蓋原有的統計數據字段，確保一致性
    checkpointCount: stats.checkpointCount,
    attentionCount: stats.attentionCount,
    favoriteCount: stats.favoriteCount,
    // 保留原有的 hasSRTCache 屬性（如果存在）
    hasSRTCache: video.hasSRTCache || false
  };
}

/**
 * 🆕 檢查影片是否有字幕緩存（異步版本）
 * @param video 影片數據對象
 * @returns Promise<帶有 hasSRTCache 屬性的影片對象>
 */
export async function addUnifiedStatsWithSRTCheck(video: any): Promise<any> {
  const stats = calculateVideoStats(video);
  
  // 檢查字幕緩存
  let hasSRTCache = video.hasSRTCache || false;
  
  // 🔧 如果沒有 hasSRTCache 屬性，動態檢查
  if (!hasSRTCache && video.videoId) {
    try {
      const { hasAnySRTCache } = await import('../../../services/srtCacheService');
      hasSRTCache = await hasAnySRTCache(video.videoId);
    } catch (error) {
      console.error('❌ 檢查字幕緩存失敗:', error);
    }
  }
  
  return {
    ...video,
    // 添加統一的統計數據字段
    unifiedCheckpointCount: stats.checkpointCount,
    unifiedAttentionCount: stats.attentionCount,
    unifiedFavoriteCount: stats.favoriteCount,
    // 覆蓋原有的統計數據字段，確保一致性
    checkpointCount: stats.checkpointCount,
    attentionCount: stats.attentionCount,
    favoriteCount: stats.favoriteCount,
    // 添加字幕緩存檢測結果
    hasSRTCache
  };
}
